import type { Dart, X01Config } from './types';

/** Highest score possible in one turn: three treble twenties. */
export const MAX_TURN_POINTS = 180;

export function dartValue(dart: Dart): number {
  return dart.segment * dart.multiplier;
}

export function sumDarts(darts: readonly Dart[]): number {
  return darts.reduce((total, dart) => total + dartValue(dart), 0);
}

/** A dart is a double if it is a double segment or the 50 (double bull). */
export function isDoubleDart(dart: Dart): boolean {
  return dart.multiplier === 2;
}

/** Whether a single dart is physically possible on a board. */
export function isValidDart(dart: Dart): boolean {
  if (dart.segment === 0) {
    return dart.multiplier === 1; // a miss
  }
  if (dart.segment === 25) {
    return dart.multiplier === 1 || dart.multiplier === 2; // bull / double bull
  }
  return dart.segment >= 1 && dart.segment <= 20;
}

export type TurnOutcome =
  | { readonly kind: 'score'; readonly remaining: number }
  | { readonly kind: 'win' }
  | { readonly kind: 'bust'; readonly reason: string };

/**
 * The core X01 rule, applied to one side's remaining score.
 *
 *   below zero            → bust
 *   exactly zero          → win, but only on a double when double-out is on
 *   one left (double-out) → bust, because 1 cannot be finished on a double
 *   otherwise             → the new remaining score
 *
 * `finishedOnDouble` is what the counter derives (dart entry) or trusts (sum
 * entry); everything else follows from the numbers alone.
 */
export function resolveTurn(
  remaining: number,
  points: number,
  finishedOnDouble: boolean,
  config: X01Config,
): TurnOutcome {
  if (points < 0 || points > MAX_TURN_POINTS) {
    return { kind: 'bust', reason: 'Ungültige Punktzahl' };
  }

  const next = remaining - points;

  if (next < 0) {
    return { kind: 'bust', reason: 'Überworfen' };
  }

  if (next === 0) {
    if (config.doubleOut && !finishedOnDouble) {
      return { kind: 'bust', reason: 'Kein Doppel zum Ausmachen' };
    }
    return { kind: 'win' };
  }

  if (next === 1 && config.doubleOut) {
    return { kind: 'bust', reason: 'Rest 1 ist nicht ausspielbar' };
  }

  return { kind: 'score', remaining: next };
}
